'use client';

import React, { useState } from 'react';

interface AspAdminItemProps {
  codeName: string;
  description?: string;
  aspValue?: number | string;
  isDrug?: boolean;
  error?: string;
  onAspValueChange: (code: string, value: string) => void;
}

const AspAdminItem: React.FC<AspAdminItemProps> = ({
  codeName,
  description,
  aspValue,
  isDrug = false,
  error,
  onAspValueChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [editValue, setEditValue] = useState('');

  const displayValue = aspValue
    ? new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(aspValue))
    : '0.00';

  return (
    <div className="self-stretch p-4 bg-white rounded-lg outline outline-1 -outline-offset-1 outline-neutral-300 inline-flex justify-start items-center gap-4 hover:outline-neutral-400 transition-colors">
      <div className="flex-1 inline-flex flex-col justify-center items-start gap-2">
        <div className="justify-start text-primary text-sm font-normal font-['Inter'] leading-5">
          {codeName}
        </div>
        {description && (
          <div className="self-stretch justify-start leading-tight">
            <span className="text-tertiary text-xs font-normal font-['Inter']">
              {!isExpanded && description.length > 60
                ? `${description.substring(0, 60)}... `
                : `${description} `}
            </span>
            {description.length > 60 && (
              <span
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-secondary text-xs font-normal font-['Inter'] underline hover:text-primary-dark transition-colors cursor-pointer"
              >
                {isExpanded ? 'show less' : 'show more'}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="w-40 min-w-36 inline-flex flex-col justify-start items-start gap-1">
        <div className="self-stretch inline-flex justify-between items-center">
          <div className="flex-1 flex justify-start items-center">
            <div className="justify-start text-primary text-xs font-semibold font-['Inter'] leading-4">
              Average Sale Price
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1 w-full">
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center">
              <span
                className={`text-sm font-normal font-['Inter'] leading-none ${
                  isDrug ? 'text-neutral-400' : 'text-neutral-600'
                }`}
              >
                $
              </span>
            </div>
            <input
              type="text"
              placeholder={isDrug ? 'N/A for drugs' : 'Enter ASP value'}
              value={isFocused ? editValue : displayValue}
              onFocus={() => {
                setIsFocused(true);
                setEditValue(aspValue ? String(aspValue) : '');
              }}
              onChange={(e) => {
                setEditValue(e.target.value);
                onAspValueChange(codeName, e.target.value);
              }}
              onBlur={() => {
                setIsFocused(false);
              }}
              disabled={isDrug}
              className={`w-full pl-6 pr-3 py-2 bg-white rounded-full outline outline-1 -outline-offset-1
                    text-sm font-normal font-['Inter'] leading-5 focus:outline-2
                    focus:outline-primary disabled:bg-neutral-100 disabled:cursor-not-allowed disabled:text-neutral-500 ${
                      error
                        ? 'outline-red-500 focus:outline-red-500'
                        : 'outline-neutral-300'
                    }`}
            />
          </div>
          {error && <span className="text-xs text-red-500">{error}</span>}
        </div>
      </div>
    </div>
  );
};

export default AspAdminItem;
